import { useDroppable } from "@dnd-kit/core";
import { faLayerGroup } from "@fortawesome/pro-solid-svg-icons";
import React from "react";
import { useParams } from "react-router-dom";

import {
  Breadcrumb,
  Breadcrumbs,
  Icon,
  useTheme,
} from "../../../components/ui-v2";
import { useDrive } from "../DriveContext";

type DriveBreadcrumbPart = {
  name: string;
  icon?: React.ReactElement;
  path: string;
};

type DriveBreadcrumbProps = {
  name: string;
  path: string;
  icon?: React.ReactElement;
  isLast: boolean;
};

const DriveBreadcrumb = ({
  name,
  path,
  icon,
  isLast,
}: DriveBreadcrumbProps) => {
  const theme = useTheme();
  const { workspace: workspaceName } = useParams();
  const { setCurrentDirectoryPath } = useDrive();
  const { isOver, setNodeRef: droppableNodeRef } = useDroppable({
    id: `directory__${path}`,
    disabled: isLast,
    data: {
      type: "directory",
      path: path || "/",
    },
  });

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    // Prevent default to avoid page reload and modal close if the breadcrumb is clicked.
    event.preventDefault();
    if (setCurrentDirectoryPath) {
      setCurrentDirectoryPath(path);
    }
  };

  return (
    <span ref={droppableNodeRef}>
      <Breadcrumb
        href={`/${workspaceName}/drive${path}`}
        sx={{
          transition: "transform 200ms ease-out",
          ...(isOver && {
            color: theme.palette.primary.light,
            animation: "bounce 1s infinite",
          }),
          "@keyframes bounce": {
            "0%, 100%": {
              transform: "translateY(0)",
              animationTimingFunction: "cubic-bezier(0.8, 0, 1, 1)",
            },
            "25%": {
              transform: "translateY(-25%)",
              animationTimingFunction: "cubic-bezier(0.8, 0, 1, 1)",
            },
            "50%": {
              transform: "translateY(0)",
              animationTimingFunction: "cubic-bezier(0, 0, 0.2, 1)",
            },
            "75%": {
              transform: "translateY(-12.5%)",
              animationTimingFunction: "cubic-bezier(0.8, 0, 1, 1)",
            },
          },
        }}
        onClick={handleClick}
        label={name}
        icon={icon}
      />
    </span>
  );
};

export const DriveBreadcrumbs = () => {
  const { currentDirectoryPath } = useDrive();
  const directoryParentNames = currentDirectoryPath
    .split("/")
    .filter((x) => x !== "");
  const directoryParents: DriveBreadcrumbPart[] = directoryParentNames.reduce(
    (parents: DriveBreadcrumbPart[], name) => {
      const parentDirectory = parents[parents.length - 1].path;
      parents.push({ name, path: `${parentDirectory}/${name}` });
      return parents;
    },
    [
      {
        name: "Drive",
        icon: <Icon icon={faLayerGroup} />,
        path: "",
      },
    ],
  );

  return (
    <Breadcrumbs sx={{ flexShrink: 0 }} data-testid="drive-breadcrumb">
      {directoryParents.map((parent, index) => {
        const isLast = index === directoryParents.length - 1;
        return (
          <DriveBreadcrumb
            key={parent.path}
            name={parent.name}
            icon={parent.icon}
            path={parent.path}
            isLast={isLast}
          />
        );
      })}
    </Breadcrumbs>
  );
};
